import { FC } from 'react'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  Button,
  ButtonIcon,
  CustomSelect,
  ErrorLabel,
  IOption,
  Label,
} from '../../../../../../../components'
import { ButtonType, IconPaths, IconSize } from '../../../../../../../../_metronic/helpers'
import { CondicaoCatalogo } from '../../../../core/condicao/catalogo'
import {
  Condicao,
  Grupo,
  TipoGrupo,
  itensDoGrupo,
  montarGrupo,
  tipoDoGrupo,
} from '../../../../core/condicao/tipos'
import { ErroPosicionado, errosDoNo } from '../../../../core/condicao/caminho'
import { CondicaoLinha } from './CondicaoLinha'
import { CampoReferenciavel, LINHA_NOVA, LinhaEmEdicao, ehLinha } from './tiposEditor'

type Props = {
  grupo: Grupo
  /** Índices do grupo na árvore: `[]` no grupo raiz, `[i]` num grupo aninhado. */
  indices: number[]
  campos: CampoReferenciavel[]
  catalogo: CondicaoCatalogo
  erros: ErroPosicionado[]
  disabled?: boolean
  /** Só o grupo raiz oferece "Adicionar grupo" (a tela limita a um nível de aninhamento). */
  permiteGrupo?: boolean
  onChange: (grupo: Grupo | null) => void
}

export const CondicaoGrupo: FC<Props> = ({
  grupo,
  indices,
  campos,
  catalogo,
  erros,
  disabled,
  permiteGrupo,
  onChange,
}) => {
  const { t } = useT()

  const tipo = tipoDoGrupo(grupo)
  const itens = itensDoGrupo(grupo)
  const nivel = indices.length
  const idTipo = `condicao-grupo-${indices.join('-') || 'raiz'}-tipo`
  const opcoesTipo: IOption<TipoGrupo>[] = [
    { value: 'todos', label: t('formularios.condicao.grupo.todos') },
    { value: 'algum', label: t('formularios.condicao.grupo.algum') },
  ]

  const substituir = (proximos: Condicao[]) =>
    onChange(proximos.length === 0 ? null : montarGrupo(tipo, proximos))

  const trocarItem = (indice: number, item: Condicao | null) =>
    substituir(
      item === null
        ? itens.filter((_, i) => i !== indice)
        : itens.map((atual, i) => (i === indice ? item : atual))
    )

  const mensagens = errosDoNo(erros, indices)
    .filter((erro) => !erro.posicao?.chave)
    .map((erro) => erro.mensagem)

  return (
    <div className={`condicao-grupo ${nivel > 0 ? 'condicao-grupo--aninhado' : ''}`}>
      <div className='condicao-grupo__cabecalho'>
        <Label className='me-2' htmlFor={idTipo}>
          {nivel === 0
            ? t('formularios.condicao.grupo.tipo')
            : t('formularios.condicao.grupo.tipo.aninhado', { numero: (indices.at(-1) ?? 0) + 1 })}
        </Label>
        <CustomSelect
          inputId={idTipo}
          className='condicao-grupo__tipo'
          isDisabled={disabled}
          isClearable={false}
          options={opcoesTipo}
          value={opcoesTipo.find((opcao) => opcao.value === tipo)}
          onChange={(opcao: IOption<TipoGrupo> | null) =>
            opcao && onChange(montarGrupo(opcao.value, itens))
          }
        />
        {nivel > 0 && (
          <ButtonIcon
            className='p-0 m-0 ms-auto'
            icon={IconPaths.Trash}
            iconSize={IconSize.Small}
            disabled={disabled}
            ariaLabel={t('formularios.condicao.grupo.remover', {
              numero: (indices.at(-1) ?? 0) + 1,
            })}
            onClick={() => onChange(null)}
          />
        )}
      </div>

      <div className='condicao-grupo__itens'>
        {itens.map((item, indice) =>
          ehLinha(item) ? (
            <CondicaoLinha
              key={`linha-${indice}`}
              linha={item as LinhaEmEdicao}
              numero={indice + 1}
              campos={campos}
              catalogo={catalogo}
              erros={errosDoNo(erros, [...indices, indice])}
              disabled={disabled}
              onChange={(linha) => trocarItem(indice, linha)}
              onRemover={() => trocarItem(indice, null)}
            />
          ) : (
            <CondicaoGrupo
              key={`grupo-${indice}`}
              grupo={item as Grupo}
              indices={[...indices, indice]}
              campos={campos}
              catalogo={catalogo}
              erros={erros}
              disabled={disabled}
              onChange={(filho) => trocarItem(indice, filho)}
            />
          )
        )}
      </div>

      {mensagens.map((mensagem) => (
        <ErrorLabel key={mensagem}>{mensagem}</ErrorLabel>
      ))}

      {!disabled && (
        <div className='condicao-grupo__acoes'>
          <Button
            type={ButtonType.Button}
            className='btn-secondary'
            size='btn-sm'
            onClick={() => substituir([...itens, { ...LINHA_NOVA }])}
          >
            {t('formularios.condicao.adicionar')}
          </Button>
          {permiteGrupo && (
            <Button
              type={ButtonType.Button}
              className='btn-secondary'
              size='btn-sm'
              onClick={() => substituir([...itens, { todos: [{ ...LINHA_NOVA }] }])}
            >
              {t('formularios.condicao.adicionar.grupo')}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
